import React, { useState } from "react";
import InputTimeField from "../styles/components/InputTime";

interface InputTimeProps {
    value: string;
    onChange: (value: string) => void;
    minValue: string;
    maxValue: string;
    id: string;
    className?: string;
}

const InputTime: React.FC<InputTimeProps> = ({
    value,
    onChange,
    minValue,
    maxValue,
    id,
    className = "p-filled",
}) => {
    const [formattedValue, setFormattedValue] = useState(value);
    const [selectionRange, setSelectionRange] = useState<
        [number, number] | null
    >(null);

    const handleSelect = (event: React.SyntheticEvent<HTMLInputElement>) => {
        const { selectionStart, selectionEnd } = event.currentTarget;

        if (selectionStart !== null && selectionEnd !== null) {
            event.currentTarget.setSelectionRange(
                formattedValue.length,
                formattedValue.length
            );
            setSelectionRange([selectionStart, selectionEnd]);
        }
    };

    const handleMouseDown = (event: React.MouseEvent<HTMLInputElement>) => {
        if (selectionRange) {
            event.preventDefault();
        }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        event.target.setSelectionRange(
            formattedValue.length,
            formattedValue.length
        );
    };

    const handleBlur = () => {
        setSelectionRange(null);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (
            event.currentTarget.selectionStart !== null &&
            event.currentTarget.selectionStart <= 1 &&
            event.key === "Backspace"
        ) {
            event.preventDefault();
        }
    };

    const removeExtraColon = (inputValue: string) => {
        return inputValue.split(":").filter(Boolean).join(":").substr(0, 5);
    };

    const cleanInputValue = (inputValue: string) => {
        return removeExtraColon(inputValue.replace(/[^\d:]/g, ""));
    };

    const limitInputValue = (
        inputValue: string,
        hour: string,
        minute: string
    ) => {
        const maxLimit = maxValue.split(":")[0];
        const minLimit = minValue.split(":")[1];
        const validatedHourParam = `${hour || "00"}`;

        if (minute && minute.length < 2) {
            return `${validatedHourParam}:${minute}`;
        }

        if (hour && Number(hour) >= 2 && minute && minute.length > 1) {
            return `${maxLimit}:00`;
        }

        if (minute && Number(minute) >= 59) {
            return `${validatedHourParam}:59`;
        }

        if (
            minute &&
            minute.length === 2 &&
            Number(minute) < Number(minLimit) &&
            Number(hour) < 1
        ) {
            return `${validatedHourParam}:${minLimit}`;
        }

        return inputValue;
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;

        let newFormattedValue = formattedValue;

        const formatted = cleanInputValue(inputValue);

        const [hour, minute] = formatted.split(":");

        newFormattedValue = limitInputValue(formatted, hour, minute);

        setFormattedValue(newFormattedValue);
        onChange(newFormattedValue);
    };

    return (
        <InputTimeField
            mask="99:99"
            type="text"
            value={formattedValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            id={id}
            className={className}
            unselectable="on"
            onMouseDown={handleMouseDown}
            onSelect={handleSelect}
            onBlur={handleBlur}
            data-testid="input-time"
        />
    );
};

export default InputTime;
